/**
 * RequestList — shared component backing all 5 /processes/requests pages.
 *
 * MyRequests / PendingApprovals / RejectedRequests / DelegatedRequests /
 * RequestHistory each fetch the same endpoint and slice client-side.
 * Actions: Approve (optional notes), Reject (required reason), Delegate
 * (assignee + optional justification + optional expiry).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Avatar, Box, Button, Chip, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Grid,
  IconButton, InputLabel, MenuItem, Paper, Select, Snackbar, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TablePagination,
  TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  AccountTreeOutlined, CancelOutlined, CheckCircleOutlined, PersonOutlined,
  Refresh as RefreshIcon, SwapHorizOutlined, Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { ApiError, organizationApi, processesApi } from '../../../api';
import { isActionableRequestStatus } from '../../../api/processes';
import type { ProcessRequest } from '../../../api/processes';
import type { WorkerResponse } from '../../../api/organization';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  getSystemRequestTitle,
  translateLiveStatus,
  translatePriority,
} from '../processDisplay';
import { translateApiError } from '../../../i18n/translateApiError';
import {
  safeAttachmentFilename,
  type ProcessAttachmentMeta,
} from '../../WorkerPortal/ProcessFormResourceValue';
import SubmittedApplication from '../../../components/process/SubmittedApplication';
import { downloadBlob } from '../../../utils/downloadFile';
import { fmtUnit } from '../../../utils/datetime';

export type RequestAction = 'approve' | 'reject' | 'delegate';

export interface RequestSummaryCard {
  id: string;
  title: string;
  value: string;
  description?: string;
  color?: 'primary' | 'success' | 'warning' | 'error' | 'info' | 'secondary';
}

export interface RequestListProps {
  title: string;
  description: string;
  /**
   * Which statuses this page scopes to. `readonly` so the shared
   * `ACTIONABLE_REQUEST_STATUSES` tuple can be passed without a copy.
   */
  statuses: readonly string[];
  /** If true, only show requests where the active user is current_approver. */
  approverOnly?: boolean;
  /** If true, only show requests where the active user is the requester. */
  requesterMe?: boolean;
  allowedActions?: RequestAction[];
  buildSummary?: (rows: ProcessRequest[]) => RequestSummaryCard[];
}

function statusColor(s: string | null | undefined): 'success' | 'info' | 'warning' | 'error' | 'default' {
  if (s === 'approved' || s === 'completed') return 'success';
  if (s === 'in_review') return 'info';
  if (s === 'submitted' || s === 'delegated') return 'warning';
  if (s === 'rejected' || s === 'cancelled') return 'error';
  return 'default';
}

function priorityColor(p: string | null | undefined): 'error' | 'warning' | 'info' | 'default' {
  if (p === 'critical' || p === 'urgent' || p === 'high') return 'error';
  if (p === 'medium') return 'warning';
  if (p === 'low') return 'info';
  return 'default';
}

function fmtDate(iso: string | null | undefined, locale: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '—';
  return d.toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' });
}

function ageString(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = Date.parse(iso);
  if (!Number.isFinite(d)) return '—';
  const diff = Date.now() - d;
  const h = diff / 3_600_000;
  // Localized units (`5小时` / `3 日`), not English `5h` / `3d`.
  if (h < 1) return fmtUnit(Math.round(h * 60), 'minute', undefined, 0);
  if (h < 24) return fmtUnit(Math.round(h), 'hour', undefined, 0);
  return fmtUnit(Math.round(h / 24), 'day', undefined, 0);
}

/**
 * One payload value, as a person reads it.
 *
 * `String(value)` was the whole implementation, which printed the literal `[object Object]`
 * for an `organization_location` — the value is a snapshot object, and the branch that
 * would have rendered it properly was unreachable because the field's TYPE was unknown
 * (the response never carried `form_schema`, fixed on the backend in the same pass).
 *
 * An object reaching here now means the schema still could not name the field, so it says
 * so instead of pasting a brace — the same choice `formatReportCell` already made for the
 * attendance reports, and for the same reason.
 */
function fullName(e: WorkerResponse | undefined): string {
  if (!e) return '—';
  return `${e.first_name} ${e.last_name}`.trim();
}

/**
 * WHO the step is waiting on — a person when one is resolved, otherwise HOW it routes.
 *
 * Reported 2026-09-08: this column read `Approver —` on a request the phone described as
 * `Role: Process Owner`. A step routed to a role or a manager LEVEL has no single
 * `current_approver_id`, so the projection was right to be null and this column simply had no second
 * thing to say.
 *
 * The descriptor is `build_resolver_config`'s output, resolved server-side, and the phrasing comes from
 * this app's own `flowResolver.*` catalogue — the same keys the flow diagram already uses, so the
 * modal and the diagram cannot describe one step in two vocabularies.
 *
 * Prefers the resolved PERSON: once the engine has picked someone, their name is more useful than the
 * rule that picked them.
 */
export function approverDisplay(
  detail: { current_approver_id?: string | null; current_approver_descriptor?: {
    kind?: string; level?: number | null; role_code?: string | null;
  } | null },
  workerById: Map<string, WorkerResponse>,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  const person = detail.current_approver_id
    ? workerById.get(detail.current_approver_id)
    : undefined;
  if (person) return fullName(person);

  const d = detail.current_approver_descriptor;
  if (!d?.kind) return '—';
  if (d.kind === 'role') {
    return t('processes:instanceDetail.flowResolver.role', { role: d.role_code ?? '—' });
  }
  if (d.kind === 'manager_chain') {
    const level = Number(d.level ?? 1);
    return level === 1
      ? t('processes:instanceDetail.flowResolver.directManager')
      : t('processes:instanceDetail.flowResolver.managerLevel', { level });
  }
  if (d.kind === 'specific_worker') {
    // Named in the flow but not yet resolved to a row we hold. "Selected person" is honest; the id is not
    // useful to a reader and the raw `wrk_…` is exactly what this whole round has been removing.
    return t('processes:instanceDetail.flowResolver.specific');
  }
  return '—';
}

const RequestList: React.FC<RequestListProps> = ({
  title, description, statuses, approverOnly, requesterMe,
  allowedActions = [], buildSummary,
}) => {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const openRequestId = searchParams.get('openId');
  const [rows, setRows] = useState<ProcessRequest[]>([]);
  const [workers, setWorkers] = useState<WorkerResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const [filter, setFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState<'all' | 'urgent' | 'high' | 'medium' | 'low'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'submitted' | 'in_review' | 'approved' | 'rejected' | 'delegated' | 'draft'>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [detail, setDetail] = useState<ProcessRequest | null>(null);

  const [approveReq, setApproveReq] = useState<ProcessRequest | null>(null);
  const [approveNotes, setApproveNotes] = useState('');

  const [rejectReq, setRejectReq] = useState<ProcessRequest | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const [delegateReq, setDelegateReq] = useState<ProcessRequest | null>(null);
  const [delegateTo, setDelegateTo] = useState('');
  const [delegateJustification, setDelegateJustification] = useState('');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [reqs, workers] = await Promise.all([
        processesApi.processRequestsApi.list({
          ...(requesterMe ? { requester_id: 'me' } : {}),
          ...(approverOnly ? { assigned_to: 'me' } : {}),
        }),
        organizationApi.workersApi.list('active'),
      ]);
      setRows(reqs);
      setWorkers(workers);
    } catch (e) {
      setError(e instanceof ApiError ? translateApiError(e, t) : t('processes:requestList.loadError'));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [requesterMe, approverOnly, t]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    if (!openRequestId) return;
    const requested = rows.find((row) => row.id === openRequestId);
    if (requested) setDetail(requested);
  }, [openRequestId, rows]);

  const workerById = useMemo(
    () => new Map(workers.map((e) => [e.id, e])),
    [workers],
  );

  const scoped = useMemo(
    () => rows.filter((r) => statuses.includes(r.status)),
    [rows, statuses],
  );

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return scoped.filter((r) => {
      if (priorityFilter !== 'all' && (r.priority ?? '') !== priorityFilter) return false;
      if (statusFilter !== 'all' && (r.status ?? '') !== statusFilter) return false;
      if (!q) return true;
      return (
        getSystemRequestTitle(t, r, r.title).toLowerCase().includes(q) ||
        (r.requester_id ?? '').toLowerCase().includes(q)
      );
    });
  }, [scoped, filter, priorityFilter, statusFilter, t]);

  const paginated = useMemo(
    () => filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filtered, page, rowsPerPage],
  );

  const summary = useMemo(
    () => (buildSummary ? buildSummary(scoped) : []),
    [buildSummary, scoped],
  );

  const handleApprove = async () => {
    if (!approveReq) return;
    try {
      await processesApi.processRequestsApi.approve(approveReq.id, { notes: approveNotes || null });
      setToast(t('processes:requestList.approved', { title: getSystemRequestTitle(t, approveReq, approveReq.title) }));
      setApproveReq(null);
      setApproveNotes('');
      await fetchAll();
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('processes:requestList.approveFailed'));
    }
  };

  const handleReject = async () => {
    if (!rejectReq || !rejectReason.trim()) return;
    try {
      await processesApi.processRequestsApi.reject(rejectReq.id, { reason: rejectReason });
      setToast(t('processes:requestList.rejected', { title: getSystemRequestTitle(t, rejectReq, rejectReq.title) }));
      setRejectReq(null);
      setRejectReason('');
      await fetchAll();
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('processes:requestList.rejectFailed'));
    }
  };

  const handleDelegate = async () => {
    if (!delegateReq || !delegateTo) return;
    try {
      await processesApi.processRequestsApi.delegate(delegateReq.id, {
        delegated_to: delegateTo,
        justification: delegateJustification || null,
      });
      setToast(t('processes:requestList.delegated', { name: fullName(workerById.get(delegateTo)) }));
      setDelegateReq(null);
      setDelegateTo('');
      setDelegateJustification('');
      await fetchAll();
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('processes:requestList.delegateFailed'));
    }
  };

  const handleDownloadAttachment = async (attachment: ProcessAttachmentMeta) => {
    try {
      const blob = await processesApi.processAttachmentsApi.download(attachment.id);
      downloadBlob(blob, safeAttachmentFilename(attachment.file_name));
    } catch (caught) {
      setToast(caught instanceof ApiError
        ? translateApiError(caught, t)
        : t('processes:requestList.downloadFailed'));
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: '12px', width: '100%' }}>
      <Box sx={{ width: '100%', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 1.5, mb: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem', color: 'text.primary' }}>{title}</Typography>
          <Typography variant="body2" color="text.secondary">{description}</Typography>
        </Box>
        <Tooltip title={t('common:actions.refresh')}>
          <span>
            <IconButton size="small" onClick={fetchAll} disabled={loading}
              sx={{ border: '1px solid rgba(0,0,0,0.2)', borderRadius: 1, bgcolor: '#ffffff', '&:hover': { bgcolor: '#f5f5f5' } }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {error && <Alert severity="error" sx={{ width: '100%', mb: 2 }}>{error}</Alert>}

      {summary.length > 0 && (
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2.5, flexWrap: 'wrap' }}>
          {summary.flatMap((s, i) => [
            i > 0 ? <Typography key={`sep-${s.id}`} variant="caption" color="text.disabled">·</Typography> : null,
            <Typography key={s.id} variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box component="strong" sx={{ color: `${s.color ?? 'primary'}.main`, fontWeight: 700 }}>{s.value}</Box>
              {s.title}
            </Typography>,
          ]).filter(Boolean)}
        </Stack>
      )}

      {/* Filter bar — FILTER_DESIGN_CONVENTION §3 */}
      <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', mb: 1, gap: 1.5, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>{t('processes:tasks.priorityLabel')}</InputLabel>
          <Select value={priorityFilter} label={t("processes:tasks.priorityLabel")}
            onChange={(e) => { setPriorityFilter(e.target.value as typeof priorityFilter); setPage(0); }}>
            <MenuItem value="all">{t("processes:tasks.allPriorities")}</MenuItem>
            <MenuItem value="urgent">{t("processes:tasks.urgent")}</MenuItem>
            <MenuItem value="high">{t("processes:tasks.high")}</MenuItem>
            <MenuItem value="medium">{t("processes:tasks.medium")}</MenuItem>
            <MenuItem value="low">{t("processes:tasks.low")}</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>{t('processes:requests.statusLabel')}</InputLabel>
          <Select value={statusFilter} label={t("processes:requests.statusLabel")}
            onChange={(e) => { setStatusFilter(e.target.value as typeof statusFilter); setPage(0); }}>
            <MenuItem value="all">{t("processes:requests.allStatuses")}</MenuItem>
            <MenuItem value="submitted">{t("common:status.pending")}</MenuItem>
            <MenuItem value="in_review">{t("processes:requests.inProgress")}</MenuItem>
            <MenuItem value="approved">{t("processes:requests.approved")}</MenuItem>
            <MenuItem value="rejected">{t("processes:requests.rejected")}</MenuItem>
            <MenuItem value="delegated">{translateLiveStatus(t, 'delegated')}</MenuItem>
            <MenuItem value="draft">{t("common:status.draft")}</MenuItem>
          </Select>
        </FormControl>
        <TextField size="small" variant="outlined" label={t("processes:requests.searchPlaceholder")} value={filter}
          onChange={(e) => { setFilter(e.target.value); setPage(0); }}
          sx={{ width: 280 }} />
        <Box sx={{ flex: 1 }} />
        <TablePagination component="div" count={filtered.length} page={page}
          onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[5, 10, 25, 50]}
          sx={{ '.MuiTablePagination-toolbar': { pl: 0, minHeight: 36 }, '.MuiTablePagination-displayedRows': { m: 0 } }} />
      </Box>

      {/* Chips §5 */}
      {(() => {
        const chips: { key: string; label: string; onClear: () => void }[] = [];
        if (priorityFilter !== 'all')
          chips.push({ key: 'prio', label: t('processes:tasks.priorityChip', { value: translatePriority(t, priorityFilter) }),
            onClear: () => { setPriorityFilter('all'); setPage(0); } });
        if (statusFilter !== 'all')
          chips.push({ key: 'status', label: t('processes:requests.statusChip', { value: translateLiveStatus(t, statusFilter) }),
            onClear: () => { setStatusFilter('all'); setPage(0); } });
        if (filter.trim())
          chips.push({ key: 'search', label: t('processes:requests.searchChip', { value: filter.trim() }),
            onClear: () => { setFilter(''); setPage(0); } });
        if (chips.length === 0) return null;
        return (
          <Box sx={{ width: '100%', display: 'flex', gap: 0.75, mb: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            {chips.map((c) => (
              <Chip key={c.key} label={c.label} size="small" onDelete={c.onClear}
                variant="outlined" color="primary"
                sx={{ borderRadius: 1, height: 24, fontSize: '0.75rem', fontWeight: 500 }} />
            ))}
            <Button size="small"
              onClick={() => { setPriorityFilter('all'); setStatusFilter('all'); setFilter(''); setPage(0); }}
              sx={{ textTransform: 'none', fontSize: '0.75rem', color: 'text.secondary', minWidth: 0, px: 0.5,
                '&:hover': { bgcolor: 'transparent', color: 'text.primary' } }}>
              {t('processes:requests.clearAll')}
            </Button>
          </Box>
        );
      })()}

      <TableContainer component={Paper} sx={{ width: '100%', borderRadius: 2, overflow: 'hidden' }}>
        {/* minWidth so the container SCROLLS instead of the cells wrapping. Without it a
          table narrower than its content breaks headers onto two lines and gives every
          row a different height — the raggedness the owner reported on Roles, found in
          six more tables by the module audit. 650 is what 9 of the 12 sized admin
          tables use. */}
        <Table sx={{ minWidth: 650 }}>
          <TableHead sx={{ bgcolor: 'grey.100', '& .MuiTableCell-root': { fontWeight: 600 } }}>
            <TableRow>
              <TableCell>{t('processes:requests.colRequest')}</TableCell>
              <TableCell>{t('processes:requests.colRequester')}</TableCell>
              <TableCell>{t('processes:requestList.colApprover')}</TableCell>
              <TableCell align="center">{t('processes:tasks.colPriority')}</TableCell>
              <TableCell align="center">{t('processes:requests.colStatus')}</TableCell>
              <TableCell align="center">{t('processes:overview.tableHeaders.age')}</TableCell>
              <TableCell align="center">{t('processes:requests.colActions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : paginated.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                  <Typography variant="body2">{t('processes:requests.noMatch')}</Typography>
                </TableCell>
              </TableRow>
            ) : /* The informational cells open the detail the actions column
                   already had behind an icon — clicking the row was the reader's
                   first instinct and did nothing. The actions cell keeps its own
                   handlers: one click intent per cell (svdy-ui §12.8). */
              paginated.map((r) => (
              <TableRow key={r.id} hover
                    sx={{ '& .MuiTableCell-root': { whiteSpace: 'nowrap' } }}
                  >
                {/* The prose column: bounded so it wraps at a sane measure instead of
                    stretching the row, and allowed to wrap where the row says nowrap. */}
                <TableCell
                  onClick={() => setDetail(r)}
                  sx={{ cursor: 'pointer', maxWidth: 320, whiteSpace: 'normal' }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{getSystemRequestTitle(t, r, r.title)}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('processes:requests.submitted')} {fmtDate(r.submitted_at ?? r.created_at, i18n.language)}
                  </Typography>
                </TableCell>
                <TableCell onClick={() => setDetail(r)} sx={{ cursor: 'pointer' }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Avatar sx={{ width: 24, height: 24, fontSize: '0.6rem', bgcolor: 'primary.main' }}>
                      <PersonOutlined sx={{ fontSize: 14 }} />
                    </Avatar>
                    <Typography variant="caption">{fullName(workerById.get(r.requester_id))}</Typography>
                  </Stack>
                </TableCell>
                <TableCell onClick={() => setDetail(r)} sx={{ cursor: 'pointer' }}>
                  {r.current_approver_id ? (
                    <Typography variant="caption">{fullName(workerById.get(r.current_approver_id))}</Typography>
                  ) : <Typography variant="caption" color="text.disabled">—</Typography>}
                </TableCell>
                <TableCell align="center" onClick={() => setDetail(r)} sx={{ cursor: 'pointer' }}>
                  <Chip label={translatePriority(t, r.priority)} size="small" color={priorityColor(r.priority)}
                    variant="outlined" sx={{ borderRadius: 1, height: 20, fontSize: '0.7rem', textTransform: 'capitalize' }} />
                </TableCell>
                <TableCell align="center" onClick={() => setDetail(r)} sx={{ cursor: 'pointer' }}>
                  <Chip label={translateLiveStatus(t, r.status)} size="small" color={statusColor(r.status)}
                    variant="outlined" sx={{ borderRadius: 1, height: 20, fontSize: '0.7rem', textTransform: 'capitalize' }} />
                </TableCell>
                <TableCell align="center" onClick={() => setDetail(r)} sx={{ cursor: 'pointer' }}>
                  <Typography variant="caption">{ageString(r.submitted_at ?? r.created_at)}</Typography>
                </TableCell>
                <TableCell align="center">
                  <Stack direction="row" spacing={0.5} justifyContent="center">
                    <Tooltip title={t('processes:requests.viewDetails')}>
                      <IconButton size="small" onClick={() => setDetail(r)}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {allowedActions.includes('approve') && isActionableRequestStatus(r.status) && (
                      <Tooltip title={t('processes:requests.approveTooltip')}>
                        <IconButton size="small" color="success"
                          onClick={() => { setApproveReq(r); setApproveNotes(''); }}>
                          <CheckCircleOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {allowedActions.includes('reject') && isActionableRequestStatus(r.status) && (
                      <Tooltip title={t('processes:requests.rejectTooltip')}>
                        <IconButton size="small" color="error"
                          onClick={() => { setRejectReq(r); setRejectReason(''); }}>
                          <CancelOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {allowedActions.includes('delegate') && isActionableRequestStatus(r.status) && (
                      <Tooltip title={t('processes:requestList.delegate')}>
                        <IconButton size="small" color="primary"
                          onClick={() => { setDelegateReq(r); setDelegateTo(''); setDelegateJustification(''); }}>
                          <SwapHorizOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Detail */}
      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{getSystemRequestTitle(t, detail, detail?.title)}</DialogTitle>
        <DialogContent>
          {detail && (
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              <Grid container spacing={2}>
                {[
                  { label: t('processes:requestList.detail.status'), value: translateLiveStatus(t, detail.status) },
                  { label: t('processes:requestList.detail.priority'), value: translatePriority(t, detail.priority) },
                  { label: t('processes:requestList.detail.requester'), value: fullName(workerById.get(detail.requester_id)) },
                  { label: t('processes:requestList.colApprover'), value: approverDisplay(detail, workerById, t) },
                  { label: t('processes:requestList.detail.submitted'), value: fmtDate(detail.submitted_at, i18n.language) },
                  { label: t('processes:requestList.detail.due'), value: fmtDate(detail.due_date, i18n.language) },
                  { label: t('processes:requestList.detail.currentStep'), value: detail.current_step ?? '—' },
                  { label: t('processes:requestList.detail.attachments'), value: String(detail.attachments_count) },
                  { label: t('processes:requestList.detail.comments'), value: String(detail.comments_count) },
                ].map((r) => (
                  <Grid item xs={6} key={r.label}>
                    <Typography variant="caption" color="text.secondary">{r.label}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{String(r.value)}</Typography>
                  </Grid>
                ))}
              </Grid>
              {detail.data && Object.keys(detail.data).length > 0 && (
                <SubmittedApplication
                  formData={detail.data}
                  formSchema={detail.form_schema as never}
                  attachments={detail.attachments as ProcessAttachmentMeta[]}
                  organizationCurrency={detail.organization_currency}
                  onDownload={handleDownloadAttachment}
                  t={t}
                />
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {/* THE closure link, and it is why `/processes/instances` could lose its nav
              entry: before this, the instance detail — the flow diagram, the timeline,
              cancel and reassign — was reachable only from the sidebar and from four legacy
              URL redirects. Nothing in the product linked a REQUEST to the process that
              runs it, so an approver reading a request could not see where it was stuck.
              Removing the nav entry without adding this would have created the
              destination-nothing-resolves defect (svdy-ui §12.10), which is the one thing
              a tidy-up must not do.

              Rendered only when there IS an instance: a request on the simple
              (non-engine) path has no `instance_id`, and a button that navigates nowhere
              is worse than an absent one. */}
          {detail?.instance_id && (
            <Button
              onClick={() => navigate(`/processes/instances/${detail.instance_id}`)}
              startIcon={<AccountTreeOutlined fontSize="small" />}
            >
              {t('processes:requestList.viewProcess')}
            </Button>
          )}
          <Button onClick={() => setDetail(null)}>{t('common:actions.close')}</Button>
        </DialogActions>
      </Dialog>

      {/* Approve */}
      <Dialog open={!!approveReq} onClose={() => setApproveReq(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('processes:requestList.approveRequest')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">{getSystemRequestTitle(t, approveReq, approveReq?.title)}</Typography>
            <TextField label={t('processes:requestList.notesOptional')} multiline rows={3} fullWidth
              value={approveNotes}
              onChange={(e) => setApproveNotes(e.target.value)} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveReq(null)}>{t('common:actions.cancel')}</Button>
          <Button variant="contained" color="success" onClick={handleApprove}>{t('processes:requestList.approve')}</Button>
        </DialogActions>
      </Dialog>

      {/* Reject */}
      <Dialog open={!!rejectReq} onClose={() => setRejectReq(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('processes:requestList.rejectRequest')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">{getSystemRequestTitle(t, rejectReq, rejectReq?.title)}</Typography>
            <TextField label={t('processes:requestList.reasonRequired')} required multiline rows={3} fullWidth
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectReq(null)}>{t('common:actions.cancel')}</Button>
          <Button variant="contained" color="error" onClick={handleReject}
            disabled={!rejectReason.trim()}>
            {t('processes:requestList.reject')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delegate */}
      <Dialog open={!!delegateReq} onClose={() => setDelegateReq(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('processes:requestList.delegateRequest')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">{getSystemRequestTitle(t, delegateReq, delegateReq?.title)}</Typography>
            <FormControl fullWidth size="small">
              <InputLabel>{t('processes:requestList.delegateToLabel')}</InputLabel>
              <Select value={delegateTo} label={t('processes:requestList.delegateToLabel')}
                onChange={(e) => setDelegateTo(e.target.value)}>
                {workers.map((worker) => (
                  <MenuItem key={worker.id} value={worker.id}>
                    {fullName(worker)} ({worker.worker_code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField label={t('processes:requestList.justificationOptional')} multiline rows={2} fullWidth
              value={delegateJustification}
              onChange={(e) => setDelegateJustification(e.target.value)} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDelegateReq(null)}>{t('common:actions.cancel')}</Button>
          <Button variant="contained" onClick={handleDelegate} disabled={!delegateTo}>
            {t('processes:requestList.delegate')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="info" onClose={() => setToast(null)} sx={{ width: '100%' }}>{toast}</Alert>
      </Snackbar>
    </Box>
  );
};

export default RequestList;
